import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { profile } from "@/mock/profile";
import { extraLinks } from "@/mock/extraLinks";
import {
  Instagram,
  Facebook,
  Youtube,
  Globe2,
  Mail,
  Phone,
  Link2,
  MessageCircle,
  Linkedin,
} from "lucide-react";

const iconMap = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  website: Globe2,
  email: Mail,
  phone: Phone,
  tiktok: MessageCircle,
  linkedin: Linkedin,
  default: Link2,
};

const LinkButton = ({ link }) => {
  const Icon = iconMap[link.type] || iconMap.default;

  return (
    <Button
      asChild
      variant={link.highlight ? "default" : "outline"}
      className={`link-button w-full justify-between rounded-full border-pink-100 bg-white/70 text-sm font-medium text-rose-900 shadow-sm backdrop-blur-xl transition-colors duration-200 hover:border-rose-200 hover:bg-rose-50/90 hover:text-rose-900 ${
        link.highlight ? "bg-rose-500 text-rose-50 hover:bg-rose-400 border-0" : ""
      }`}
    >
      <a href={link.href} rel="noreferrer">
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-500">
            <Icon className="h-4 w-4" />
          </span>
          <span>{link.label}</span>
        </span>
        <span className="ml-4 text-xs font-semibold uppercase tracking-[0.14em] text-rose-400">
          Open
        </span>
      </a>
    </Button>
  );
};

const LinkPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-rose-100 text-slate-900">
      <div className="relative isolate flex min-h-screen items-center justify-center px-4 py-10">
        {/* Background accents */}
        <div className="pointer-events-none absolute inset-x-8 top-10 -z-10 mx-auto h-56 max-w-xl rounded-[2.5rem] bg-pink-200/50 blur-3xl" />
        <div className="pointer-events-none absolute inset-x-12 bottom-0 -z-10 mx-auto h-40 max-w-md rounded-[3rem] bg-rose-200/60 blur-3xl" />

        <div className="relative z-10 w-full max-w-3xl">
          {/* Glass card */}
          <div className="glass-card rounded-3xl border border-white/60 bg-white/70 p-6 shadow-[0_18px_55px_rgba(244,114,182,0.30)] backdrop-blur-2xl sm:p-8">
            {/* Header */}
            <header className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              {/* Larger photo on the left */}
              <div className="relative mx-auto flex w-full items-center justify-center sm:mx-0 sm:w-auto sm:justify-start">
                <div className="absolute -inset-2 rounded-[1.75rem] bg-gradient-to-tr from-rose-200 via-pink-200 to-rose-100 opacity-80 blur-xl" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-white/70 bg-white/70 shadow-md">
                  <Avatar className="h-40 w-32 sm:h-44 sm:w-36 rounded-[1.4rem]">
                    <AvatarImage
                      src={profile.avatarUrl}
                      alt={profile.name}
                      className="h-full w-full object-cover"
                    />
                    <AvatarFallback className="bg-rose-100 text-lg font-semibold text-rose-500">
                      {profile.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>

              {/* Text content on the right */}
              <div className="mt-4 flex-1 text-center sm:mt-0 sm:text-left">
                <div className="space-y-1">
                  <h1 className="text-2xl font-semibold tracking-tight text-rose-950 sm:text-3xl">
                    {profile.name}
                  </h1>
                  <p className="text-sm font-medium uppercase tracking-[0.18em] text-rose-400">
                    {profile.handle}
                  </p>
                  <p className="text-sm text-rose-500">{profile.title}</p>
                </div>

                <p className="mt-3 max-w-xl text-sm leading-relaxed text-rose-500/80 sm:max-w-none">
                  {profile.bio}
                </p>
              </div>
            </header>

            {/* Links */}
            <main className="mt-8 space-y-3">
              {[...profile.links, ...extraLinks].map((link) => (
                <LinkButton key={link.id} link={link} />
              ))}
            </main>

            {/* Footer */}
            <footer className="mt-8 flex flex-col items-center gap-1 text-center text-[11px] text-rose-400">
              <span>Share this page with your friends and community.</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-rose-300">
                All links in one place
              </span>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LinkPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
